<template>
  <div class="reference-point-property" data-testid="assemble-point-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="选择" name="selection">
        <div class="group-block">
          <SelectionListFrame
            :items="entriesList"
            :active-index="activeEntryIndex"
            :get-item-key="getReferencePointEntryKey"
            empty-text="请选择参考"
            context-menu-enabled
            delete-all-label="消除选择"
            list-role="list"
            @update:active-index="setActiveEntry"
            @delete-all="handleClearSelections"
            @delete-item="handleDeleteSelection"
          />
        </div>
      </n-collapse-item>

      <n-collapse-item title="模式" name="mode">
        <div class="group-block" :class="{ disabled: placementDriver === 'numericXYZ' }">
          <n-radio-group
            :value="geometryMethod"
            size="small"
            :disabled="placementDriver === 'numericXYZ'"
            @update:value="handleGeometryMethodChange"
          >
            <n-space vertical size="small">
              <n-radio v-for="item in methodItems" :key="item.value" :value="item.value">
                {{ item.label }}
              </n-radio>
            </n-space>
          </n-radio-group>
        </div>

        <div v-if="geometryMethod === PointGeometryMethod.AlongCurve" class="group-block" :class="{ disabled: placementDriver === 'numericXYZ' }">
          <n-radio-group
            :value="alongCurve.distributionType"
            :disabled="placementDriver === 'numericXYZ'"
            @update:value="handleDistributionTypeChange"
          >
            <n-space vertical size="small">
              <n-radio value="distance">距离</n-radio>
              <n-radio value="percentage">百分比</n-radio>
              <n-radio value="evenlyDistributed">均匀分布</n-radio>
            </n-space>
          </n-radio-group>

          <div class="param-row">
            <span class="param-label">间距</span>
            <n-input-number
              :disabled="placementDriver === 'numericXYZ' || alongCurve.distributionType === 'evenlyDistributed'"
              :value="spacingDisplayValue"
              :min="0"
              :step="1"
              @update:value="handleSpacingChange"
            />
          </div>
          <div class="param-row">
            <span class="param-label">点数量</span>
            <n-input-number
              :disabled="placementDriver === 'numericXYZ'"
              :value="alongCurve.pointCount"
              :min="1"
              :step="1"
              @update:value="handlePointCountChange"
            />
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <n-checkbox :checked="placementDriver === 'numericXYZ'" @update:checked="handleNumericToggle">
          用数值定义位置
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item title="参数" name="xyz">
        <div class="param-grid" :class="{ disabled: placementDriver !== 'numericXYZ' }">
          <div class="param-row">
            <span class="param-label">X</span>
            <n-input-number
              :disabled="placementDriver !== 'numericXYZ'"
              :value="xyzDisplay.x"
              :step="1"
              @update:value="(value) => handleXyzChange('x', value)"
            />
          </div>
          <div class="param-row">
            <span class="param-label">Y</span>
            <n-input-number
              :disabled="placementDriver !== 'numericXYZ'"
              :value="xyzDisplay.y"
              :step="1"
              @update:value="(value) => handleXyzChange('y', value)"
            />
          </div>
          <div class="param-row">
            <span class="param-label">Z</span>
            <n-input-number
              :disabled="placementDriver !== 'numericXYZ'"
              :value="xyzDisplay.z"
              :step="1"
              @update:value="(value) => handleXyzChange('z', value)"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { NCheckbox, NCollapse, NCollapseItem, NInputNumber, NRadio, NRadioGroup, NSpace } from 'naive-ui';
import { convertMToMm, convertMmToM, PointGeometryMethod } from '@puppycad/cad-core';
import { useViewer } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';

/**
 * @description 参考点模式选项定义。
 */
interface ReferencePointModeOption {
  /**
   * 模式值。
   */
  value: PointGeometryMethod;
  /**
   * 模式标签。
   */
  label: string;
}

/**
 * @description 选择条目定义。
 */
interface SelectionEntry {
  /**
   * 槽位 ID。
   */
  slotId: string;
  /**
   * 显示标签。
   */
  label: string;
  /**
   * 实体 ID。
   */
  entityId: string;
}

defineOptions({
  inheritAttrs: false,
});

const { getViewer } = useViewer();

/**
 * @description 参考点选择槽位摘要。
 */
interface ReferencePointSelectionSlot {
  /**
   * 槽位 ID。
   */
  slotId: string;
  /**
   * 槽位中的条目集合。
   */
  values: Array<{ label: string; entityId: string }>;
}

/**
 * @description 沿曲线参数。
 */
interface ReferencePointAlongCurveParams {
  /**
   * 分布类型。
   */
  distributionType: 'distance' | 'percentage' | 'evenlyDistributed';
  /**
   * 点数量。
   */
  pointCount: number;
  /**
   * 间距值。
   */
  spacingValue?: number;
}

/**
 * @description 数值坐标参数。
 */
interface ReferencePointXyz {
  /**
   * X 坐标。
   */
  x: number;
  /**
   * Y 坐标。
   */
  y: number;
  /**
   * Z 坐标。
   */
  z: number;
}

/**
 * @description 参考点面板状态快照。
 */
interface ReferencePointPanelSnapshot {
  /**
   * 放置驱动类型。
   */
  referencePointPlacementDriver: 'geometry' | 'numericXYZ';
  /**
   * 几何模式。
   */
  referencePointGeometryMethod: PointGeometryMethod | null;
  /**
   * 选择摘要。
   */
  referencePointSelectionSummary: ReferencePointSelectionSlot[];
  /**
   * 沿曲线参数。
   */
  referencePointAlongCurveParams: ReferencePointAlongCurveParams;
  /**
   * 数值坐标。
   */
  referencePointXyz: ReferencePointXyz;
}

/**
 * @description 默认参考点快照。
 */
const DEFAULT_REFERENCE_POINT_SNAPSHOT: ReferencePointPanelSnapshot = {
  referencePointPlacementDriver: 'geometry',
  referencePointGeometryMethod: null,
  referencePointSelectionSummary: [],
  referencePointAlongCurveParams: {
    distributionType: 'distance',
    pointCount: 1,
    spacingValue: convertMmToM(10),
  },
  referencePointXyz: {
    x: 0,
    y: 0,
    z: 0,
  },
};

/**
 * @description 本地快照。
 */
const localSnapshot = ref<ReferencePointPanelSnapshot>({ ...DEFAULT_REFERENCE_POINT_SNAPSHOT });

/**
 * @description 从渲染器同步参考点快照。
 */
function syncReferencePointSnapshot(): void {
  const controller = getViewer()?.referencePointController;
  if (!controller) {
    localSnapshot.value = {
      ...DEFAULT_REFERENCE_POINT_SNAPSHOT,
      referencePointAlongCurveParams: { ...DEFAULT_REFERENCE_POINT_SNAPSHOT.referencePointAlongCurveParams },
      referencePointXyz: { ...DEFAULT_REFERENCE_POINT_SNAPSHOT.referencePointXyz },
      referencePointSelectionSummary: [],
    };
    return;
  }

  const viewModel = controller.getViewModel();
  const snapshot: ReferencePointPanelSnapshot = {
    referencePointPlacementDriver: viewModel.state.placementDriver,
    referencePointGeometryMethod: viewModel.state.geometryMethod,
    referencePointSelectionSummary: viewModel.selectionSummary ?? [],
    referencePointAlongCurveParams: viewModel.state.params.alongCurve,
    referencePointXyz: viewModel.state.params.xyz,
  };
  localSnapshot.value = {
    ...snapshot,
    referencePointSelectionSummary: (snapshot.referencePointSelectionSummary ?? []).map((slot) => ({
      slotId: String(slot.slotId ?? ''),
      values: (slot.values ?? []).map((entry) => ({
        label: String(entry.label ?? ''),
        entityId: String(entry.entityId ?? ''),
      })),
    })),
    referencePointAlongCurveParams: {
      distributionType: snapshot.referencePointAlongCurveParams?.distributionType ?? 'distance',
      pointCount: Math.max(1, Math.round(Number(snapshot.referencePointAlongCurveParams?.pointCount ?? 1))),
      spacingValue: snapshot.referencePointAlongCurveParams?.spacingValue,
    },
    referencePointXyz: {
      x: Number(snapshot.referencePointXyz?.x ?? 0),
      y: Number(snapshot.referencePointXyz?.y ?? 0),
      z: Number(snapshot.referencePointXyz?.z ?? 0),
    },
  };
}

let referencePointSyncTimer: number | null = null;

onMounted(() => {
  syncReferencePointSnapshot();
  
  /**
   * 通过轻量轮询同步控制器状态，保证“视口选择 -> 面板刷新”链路实时可见。
   */
  referencePointSyncTimer = window.setInterval(() => {
    syncReferencePointSnapshot();
  }, 120);
  
});

onBeforeUnmount(() => {
  if (referencePointSyncTimer !== null) {
    clearInterval(referencePointSyncTimer);
    referencePointSyncTimer = null;
  }
});

/**
 * @description 默认展开分组。
 */
const defaultExpanded = ['selection', 'mode', 'options', 'xyz'];

/**
 * @description 定位驱动类型。
 */
const placementDriver = computed(() => localSnapshot.value.referencePointPlacementDriver ?? 'geometry');

/**
 * @description 几何模式。
 */
const geometryMethod = computed(() => localSnapshot.value.referencePointGeometryMethod ?? null);

/**
 * @description 选择摘要。
 */
const selectionSummary = computed(() => localSnapshot.value.referencePointSelectionSummary ?? []);

/**
 * @description 沿曲线参数。
 */
const alongCurve = computed(() => localSnapshot.value.referencePointAlongCurveParams ?? {
  distributionType: 'distance' as const,
  pointCount: 1,
  spacingValue: convertMmToM(10),
});

/**
 * @description 数值坐标参数。
 */
const xyz = computed(() => localSnapshot.value.referencePointXyz ?? { x: 0, y: 0, z: 0 });


/**
 * 将底层米制长度转换为毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值。
 */
function formatLengthForPropertyManager(value: number): number {
  // 装配基准点 PropertyManager 面向用户显示毫米，底层仍使用米制。
  return Number(convertMToMm(value).toFixed(8));
}

/**
 * 将 PropertyManager 毫米输入转换为底层米制长度。
 *
 * @param value UI 输入值。
 * @returns 米制长度。
 */
function normalizeLengthFromPropertyManager(value: number): number {
  return convertMmToM(value);
}


/**
 * @description 距离分布默认间距。
 */
const DISTANCE_DEFAULT_SPACING = 10;

/**
 * @description 百分比分布默认间距。
 */
const PERCENTAGE_DEFAULT_SPACING = 10;

/**
 * @description 主间距默认值。
 */
const primarySpacingDefault = computed(() => (
  alongCurve.value.distributionType === 'percentage'
    ? PERCENTAGE_DEFAULT_SPACING
    : DISTANCE_DEFAULT_SPACING
));

/**
 * @description 间距显示值；距离模式显示毫米，百分比模式保持百分比原值。
 */
const spacingDisplayValue = computed(() => {
  const value = alongCurve.value.spacingValue ?? primarySpacingDefault.value;
  return alongCurve.value.distributionType === 'distance'
    ? formatLengthForPropertyManager(value)
    : value;
});

/**
 * @description XYZ 坐标毫米显示值。
 */
const xyzDisplay = computed<ReferencePointXyz>(() => ({
  x: formatLengthForPropertyManager(xyz.value.x),
  y: formatLengthForPropertyManager(xyz.value.y),
  z: formatLengthForPropertyManager(xyz.value.z),
}));

/**
 * @description 模式选项。
 */
const methodItems: ReferencePointModeOption[] = [
  { value: PointGeometryMethod.CircularCenter, label: '圆弧中心' },
  { value: PointGeometryMethod.CenterOfFace, label: '面中心' },
  { value: PointGeometryMethod.Intersection, label: '交叉点' },
  { value: PointGeometryMethod.Projection, label: '投影' },
  { value: PointGeometryMethod.OnPoint, label: '在点上' },
  { value: PointGeometryMethod.AlongCurve, label: '沿曲线' },
];

/**
 * @description 当前激活选择条目索引。
 */
const activeEntryIndex = ref(0);

/**
 * @description 扁平化选择条目。
 */
const entriesList = computed<SelectionEntry[]>(() => (
  (selectionSummary.value || []).flatMap((slot) => (
    (slot.values || []).map((entry) => ({ ...entry, slotId: slot.slotId }))
  ))
));


/**
 * @description 监听参考条目列表变化并同步激活项。
 * 规则：
 * 1. 列表为空时，重置为索引 0；
 * 2. 列表有值时，默认激活最后一条（即最新参考）；
 * 3. 删除导致索引越界时，自动回落到最后一条。
 * 说明：
 * - 当前需求明确“有两个时后选中的应为选中态”，因此采用“最后一条优先”策略。
 */
watch(
  entriesList,
  (nextEntries) => {
    if (nextEntries.length === 0) {
      activeEntryIndex.value = 0;
      return;
    }

    // 始终将激活项定位到当前列表尾部，保证后选中项为选中态。
    activeEntryIndex.value = nextEntries.length - 1;
  },
  { immediate: true }
);


/**
 * @description 设置当前激活选择条目。
 * @param index 条目索引。
 */
function setActiveEntry(index: number): void {
  activeEntryIndex.value = index;
}

function handleClearSelections(): void {
  const controller = getViewer()?.referencePointController;
  controller?.clearSelections();
  controller?.syncSelectionVisuals();
  syncReferencePointSnapshot();
}

function handleDeleteSelection(payload: { item: unknown; index: number }): void {
  const entry = payload.item as SelectionEntry | undefined;
  if (!entry?.entityId) {
    return;
  }
  const controller = getViewer()?.referencePointController;
  controller?.removeSelection(entry.entityId);
  controller?.syncSelectionVisuals();
  syncReferencePointSnapshot();
}

function getReferencePointEntryKey(item: unknown, index: number): string {
  const entry = item as SelectionEntry | undefined;
  if (!entry) {
    return `reference-point-entry-${index}`;
  }
  return `${entry.slotId}-${entry.entityId}`;
}

/**
 * @description 切换数值驱动开关。
 * @param checked 勾选状态。
 */
function handleNumericToggle(checked: boolean): void {
  const controller = getViewer()?.referencePointController;
  controller?.setPlacementDriver(checked ? 'numericXYZ' : 'geometry');
  controller?.syncSelectionVisuals();
  syncReferencePointSnapshot();
}

/**
 * @description 更新几何模式（使用 n-radio 控制）。
 * @param value 目标模式值。
 */
function handleGeometryMethodChange(value: PointGeometryMethod | null): void {
  if (placementDriver.value === 'numericXYZ' || !value) {
    return;
  }
  const controller = getViewer()?.referencePointController;
  controller?.setGeometryMethod(value);
  controller?.syncSelectionVisuals();
  syncReferencePointSnapshot();
}

/**
 * @description 更新沿曲线间距参数。
 * @param value 输入值。
 */
function handleSpacingChange(value: number | null): void {
  const normalizedValue = Number(value ?? primarySpacingDefault.value);
  const nextSpacingValue = alongCurve.value.distributionType === 'percentage'
    ? normalizedValue
    : (
      alongCurve.value.distributionType === 'distance'
        ? normalizeLengthFromPropertyManager(normalizedValue)
        : normalizedValue
    );
  getViewer()?.referencePointController?.setParams({
    alongCurve: {
      ...alongCurve.value,
      spacingValue: nextSpacingValue,
    },
  });
  syncReferencePointSnapshot();
}

/**
 * @description 更新沿曲线分布类型。
 * @param value 分布类型。
 */
function handleDistributionTypeChange(value: 'distance' | 'percentage' | 'evenlyDistributed'): void {
  const nextSpacing = value === 'percentage' ? PERCENTAGE_DEFAULT_SPACING : DISTANCE_DEFAULT_SPACING;
  getViewer()?.referencePointController?.setParams({
    alongCurve: {
      ...alongCurve.value,
      distributionType: value,
      spacingValue: value === 'evenlyDistributed'
        ? (alongCurve.value.spacingValue ?? convertMmToM(DISTANCE_DEFAULT_SPACING))
        : (value === 'percentage' ? nextSpacing : convertMmToM(nextSpacing)),
      pointCount: Math.max(1, Math.round(alongCurve.value.pointCount || 1)),
    },
  });
  syncReferencePointSnapshot();
}

/**
 * @description 更新沿曲线点数量。
 * @param value 点数量。
 */
function handlePointCountChange(value: number | null): void {
  
  /**
   * 点数量固定为整数语义：
   * 1. 步长固定为 1；
   * 2. 最小值固定为 1；
   * 3. 对手输小数统一按整数归一化。
   */
  const numericValue = Number(value ?? 1);
  const normalizedCount = Math.max(1, Number.isFinite(numericValue) ? Math.round(numericValue) : 1);
  
  getViewer()?.referencePointController?.setParams({
    alongCurve: {
      ...alongCurve.value,
      pointCount: normalizedCount,
    },
  });
  syncReferencePointSnapshot();
}

/**
 * @description 更新 XYZ 数值。
 * @param axis 坐标轴。
 * @param value 输入值。
 */
function handleXyzChange(axis: 'x' | 'y' | 'z', value: number | null): void {
  const nextValue = normalizeLengthFromPropertyManager(Number(value ?? 0));
  getViewer()?.referencePointController?.setParams({
    xyz: {
      ...xyz.value,
      [axis]: nextValue,
    },
  });
  syncReferencePointSnapshot();
}

</script>

<style scoped>
.reference-point-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.group-block + .group-block {
  margin-top: 12px;
}

.group-block.disabled {
  opacity: 0.5;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: center;
}

/*------ 基准点主题适配 开始------*/
.param-label {
  color: var(--n-text-color);
  font-size: 12px;
}

:deep(.n-checkbox .n-checkbox__label) {
  color: var(--n-text-color);
}

:deep(.n-input-number .n-input__input-el) {
  color: var(--n-text-color);
}

:deep(.n-radio .n-radio__label) {
  color: var(--n-text-color);
}
/*------ 基准点主题适配 结束------*/
</style>
